import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { UIProvider } from './context/UIContext';
import Navbar         from './components/Navbar';
import Footer         from './components/Footer';
import Home           from './pages/Home';
import About          from './pages/About';
import Contact        from './pages/Contact';
import Services       from './pages/Services';
import ServiceDetail  from './pages/ServiceDetail';
import Testimonials   from './pages/Testimonials';
import { Login, Register } from './pages/Auth';
import UserDashboard  from './dashboard/UserDashboard';
import AdminLayout    from './admin/AdminLayout';

function LoadingScreen() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background:'var(--color-bg)' }}>
      <div className="w-11 h-11 rounded-full border-4 border-t-[#5f9c4c] border-[var(--glass-border)] animate-spin-slow"/>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--color-muted)]">STONE BENCH</p>
    </div>
  );
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user?.role === 'admin') return <Navigate to="/admin" replace/>;
  return <>{children}</>;
}

function GuestOnly({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace/>;
  return <>{children}</>;
}

function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace/>;
  if (user.role === 'admin') return <Navigate to="/admin" replace/>;
  return <>{children}</>;
}

function AdminOnly({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user || user.role !== 'admin') return <Navigate to="/" replace/>;
  return <>{children}</>;
}

function PublicLayout({ children, noNavbar, noFooter }: { children: React.ReactNode; noNavbar?: boolean; noFooter?: boolean }) {
  return (
    <>
      {!noNavbar && <Navbar />}
      <div>
      {/* <div className={`${!noNavbar ? 'pt-[34px]' : ''}`}> */}
        {children}
      </div>
      {!noFooter && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <UIProvider>
      <ToastProvider>
        <AuthProvider>
          <Router>
          <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)]">
            <main className="flex-1">
              <Routes>
                {/* Auth pages — redirect if already logged in */}
                <Route path="/login"    element={<GuestOnly><PublicLayout noNavbar noFooter><Login/></PublicLayout></GuestOnly>}/>
                <Route path="/register" element={<GuestOnly><PublicLayout noNavbar noFooter><Register/></PublicLayout></GuestOnly>}/>

                {/* Public pages — no authentication required, but restricted for admin users */}
                <Route path="/"                  element={<PublicOnly><PublicLayout><Home/></PublicLayout></PublicOnly>}/>
                <Route path="/about"             element={<PublicOnly><PublicLayout><About/></PublicLayout></PublicOnly>}/>
                <Route path="/contact"           element={<PublicOnly><PublicLayout><Contact/></PublicLayout></PublicOnly>}/>
                <Route path="/services"          element={<PublicOnly><PublicLayout><Services/></PublicLayout></PublicOnly>}/>
                <Route path="/services/:slug"    element={<PublicOnly><PublicLayout><ServiceDetail/></PublicLayout></PublicOnly>}/>
                <Route path="/testimonials"      element={<PublicOnly><PublicLayout><Testimonials/></PublicLayout></PublicOnly>}/>

                {/* Protected pages — auth required */}
                <Route path="/dashboard/*"       element={<Protected><PublicLayout noFooter><UserDashboard/></PublicLayout></Protected>}/>
                <Route path="/admin/*"           element={<AdminOnly><AdminLayout/></AdminOnly>}/>

                <Route path="*" element={<Navigate to="/" replace/>}/>
              </Routes>
            </main>
          </div>
        </Router>
      </AuthProvider>
    </ToastProvider>
  </UIProvider>
);
}
